import { base44 } from "@/api/base44Client"; // Add this at the top
import React, { createContext, useContext, useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useDeleteWithUndo } from "@/components/shared/DeleteWithUndo";
import { useQueryClient } from "@tanstack/react-query";

// Hooks
import { useSalesQueries } from "@/hooks/useSalesQueries";
import { useSalesFilters } from "@/hooks/useSalesFilters";
import { usePaymentFilters } from "@/hooks/usePaymentFilters";
import { useSalesMutations } from "@/hooks/useSalesMutations";
import { usePaymentMutations } from "@/hooks/usePaymentMutations";

// Pure helpers
import {
  getClient as getClientHelper,
  getApartment as getApartmentHelper,
  calculateRemainingBalance,
  checkOverduePayments,
} from "@/utils/salesHelpers";
import {
  getPaymentDetails as getPaymentDetailsHelper,
  calculatePaymentDelayInfo,
  getPaymentOverdueStatus,
} from "@/utils/paymentHelpers";

const SalesAndPaymentsContext = createContext(null);

export function useSalesAndPayments() {
  const ctx = useContext(SalesAndPaymentsContext);
  if (!ctx) {
    throw new Error(
      "useSalesAndPayments must be used within SalesAndPaymentsProvider"
    );
  }
  return ctx;
}

export function SalesAndPaymentsProvider({ children }) {
  // --- Auth & routing ---
  const { canEdit } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { scheduleDelete } = useDeleteWithUndo();
  const tabFromUrl = searchParams.get("tab");

  // --- Tab state (synced with URL) ---
  const [activeTab, setActiveTab] = useState(() =>
    ["payments", "sales", "defaulted"].includes(tabFromUrl) ? tabFromUrl : "sales"
  );

  useEffect(() => {
    const tab = searchParams.get("tab");
    if (["payments", "sales", "defaulted"].includes(tab)) {
      setActiveTab(tab);
    }
  }, [searchParams]);

  const setActiveTabAndUrl = (tab) => {
    setActiveTab(tab);
    navigate(`/SalesAndPayments?tab=${tab}`, { replace: true });
  };

  // --- Dialog state (shared between page and dialogs) ---
  const [showSaleForm, setShowSaleForm] = useState(false);
  const [editingSale, setEditingSale] = useState(null);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);

  // --- Data fetching ---
  const queries = useSalesQueries();
  const {
    sales,
    clients,
    apartments,
    reservations,
    payments,
    receipts,
    defaultedSales,
    loadingSales,
    loadingPayments,
  } = queries;

  // --- Filters & computed data ---
  const salesFilters = useSalesFilters({
    sales,
    clients,
    apartments,
    reservations,
    defaultedSales,
  });

  const paymentFilters = usePaymentFilters({
    payments,
    sales,
    clients,
    apartments,
    receipts,
  });

  // --- Mutations ---
  const closeSaleForm = () => {
    setShowSaleForm(false);
    setEditingSale(null);
  };
  const closePaymentForm = () => {
    setShowPaymentForm(false);
    setEditingPayment(null);
  };

  const salesMutations = useSalesMutations({
    queryClient,
    apartments,
    onSaleFormClose: closeSaleForm,
  });

  const paymentMutations = usePaymentMutations({
    queryClient,
    sales,
    onPaymentFormClose: closePaymentForm,
  });

  // --- Thin wrappers for helpers (close over query data) ---
  const getClient = (id) => getClientHelper(id, clients);
  const getApartment = (id) => getApartmentHelper(id, apartments);
  const getPaymentDetails = (payment) =>
    getPaymentDetailsHelper(payment, sales, clients, apartments, receipts);

  // --- Handlers ---
  const handleSaleSubmit = (data) => {
    if (editingSale) {
      salesMutations.updateSaleMutation.mutate({
        id: editingSale.id,
        data,
        originalApartmentId: editingSale.apartment_id,
      });
    } else {
      salesMutations.createSaleMutation.mutate(data);
    }
  };

  const handlePaymentSubmit = (data) => {
    if (editingPayment) {
      paymentMutations.updatePaymentMutation.mutate({
        id: editingPayment.id,
        data,
        oldAmount: editingPayment.amount,
      });
    } else {
      paymentMutations.createPaymentMutation.mutate(data);
    }
  };

  const handleDeleteSale = (sale) => {
    scheduleDelete(sale, "Sale", (s) => salesMutations.deleteSaleMutation.mutate(s), {
      restoreFn: async () => {
        await base44.entities.Sale.restore(sale.id);
        queryClient.invalidateQueries({ queryKey: ["sales"] });
        queryClient.invalidateQueries({ queryKey: ["apartments"] });
        queryClient.invalidateQueries({ queryKey: ["payments"] });
        queryClient.invalidateQueries({ queryKey: ["receipts"] });
      },
    });
  };

  const handleDeletePayment = (payment) => {
    scheduleDelete(
      payment,
      "Payment",
      (p) => paymentMutations.deletePaymentMutation.mutate(p),
      {
        restoreFn: async () => {
          await base44.entities.Payment.restore(payment.id);
          queryClient.invalidateQueries({ queryKey: ["payments"] });
          queryClient.invalidateQueries({ queryKey: ["sales"] });
          queryClient.invalidateQueries({ queryKey: ["receipts"] });
        },
      }
    );
  };

  const handleDeleteDefaultedSale = (defaultedSale) => {
    scheduleDelete(
      defaultedSale,
      "Defaulted Sale",
      (ds) => salesMutations.deleteDefaultedSaleMutation.mutate(ds.id),
      {
        restoreFn: async () => {
          await base44.entities.DefaultedSale.restore(defaultedSale.id);
          queryClient.invalidateQueries({ queryKey: ["defaultedSales"] });
        },
      }
    );
  };

  // --- Build the context value ---
  const value = {
    // Auth & routing
    canEdit,
    activeTab,
    setActiveTabAndUrl,

    // Dialog state
    showSaleForm,
    setShowSaleForm,
    editingSale,
    setEditingSale,
    showPaymentForm,
    setShowPaymentForm,
    editingPayment,
    setEditingPayment,

    // Queries
    sales,
    clients,
    apartments,
    reservations,
    payments,
    receipts,
    defaultedSales,
    loadingSales,
    loadingPayments,

    // Sales filters
    ...salesFilters,

    // Payment filters
    ...paymentFilters,

    // Mutations
    ...salesMutations,
    ...paymentMutations,

    // Handlers
    handleSaleSubmit,
    handlePaymentSubmit,
    handleDeleteSale,
    handleDeletePayment,
    handleDeleteDefaultedSale,

    // Helpers
    getClient,
    getApartment,
    getPaymentDetails,
    calculatePaymentDelayInfo,
    getPaymentOverdueStatus,
    checkOverduePayments,
    calculateRemainingBalance,
  };

  return (
    <SalesAndPaymentsContext.Provider value={value}>
      {children}
    </SalesAndPaymentsContext.Provider>
  );
}